// src/pages/admin/Notifications.jsx
import React, { useState, useEffect, useContext } from "react";
import {
  FiBell,
  FiCheck,
  FiTrash2,
  FiAlertCircle,
  FiShoppingBag,
  FiPackage,
  FiDollarSign,
  FiUser,
} from "react-icons/fi";
import axios from "axios";
import "../../../assets/css/admin/screens/adminnotifications/AdminNotifications.css";
import AdminMasterLayout from "../../../layouts/admin/AdminMasterLayout";
import MyContext from "../../../contexts/AppContext";

const getConfig = (title) => {
  const t = (title || "").toLowerCase();
  if (t.includes("shop") || t.includes("store"))
    return {
      type: "shop",
      icon: <FiShoppingBag />,
      className: "an-shop",
      label: "Shop",
      dotColor: "#f97316",
    };
  if (t.includes("product"))
    return {
      type: "product",
      icon: <FiPackage />,
      className: "an-product",
      label: "Product",
      dotColor: "#8b5cf6",
    };
  if (t.includes("topup") || t.includes("payment"))
    return {
      type: "topup",
      icon: <FiDollarSign />,
      className: "an-topup",
      label: "Topup",
      dotColor: "#0ea5e9",
    };
  if (t.includes("ticket") || t.includes("support") || t.includes("complaint"))
    return {
      type: "complaint",
      icon: <FiAlertCircle />,
      className: "an-complaint",
      label: "Complaint",
      dotColor: "#ef4444",
    };
  return {
    type: "general",
    icon: <FiBell />,
    className: "an-general",
    label: "General",
    dotColor: "#6b7280",
  };
};

const formatTime = (dateStr) => {
  const now = new Date();
  const date = new Date(dateStr);
  const mins = Math.floor((now - date) / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} minute${mins > 1 ? "s" : ""} ago`;
  if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  return date.toLocaleDateString("en-PK", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const TABS = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
  { key: "shop", label: "Shops" },
  { key: "product", label: "Products" },
  { key: "topup", label: "Topup" },
  { key: "complaint", label: "Complaints" },
];

const Notifications = () => {
  const { baseUrl, clearUnread, adminAuthToken } = useContext(MyContext);
  const [activeTab, setActiveTab] = useState("all");
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletedIds, setDeletedIds] = useState([]);

  const fetchNotifications = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/admin/notifications/viewAll`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminAuthToken}`,
        },
      })
      .then((res) => {
        setNotifications(res.data.notifications || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchNotifications();
    clearUnread("notifications");
  }, []);

  const visibleNotifs = notifications.filter((n) => !deletedIds.includes(n.id));
  const unreadCount = visibleNotifs.filter((n) => n.status === "unread").length;

  const markAllRead = () => {
    axios
      .post(
        `${baseUrl}/admin/notifications/markAllRead`,
        {}, // empty body
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminAuthToken}`,
          },
        }
      )
      .then(() => {
        setNotifications((prev) => prev.map((n) => ({ ...n, status: "read" })));
      })
      .catch(() => {});
  };

  const deleteNotif = (id) => setDeletedIds((prev) => [...prev, id]);
  const clearAll = () => setDeletedIds(visibleNotifs.map((n) => n.id));

  const getFiltered = () => {
    const base = visibleNotifs;
    if (activeTab === "all") return base;
    if (activeTab === "unread")
      return base.filter((n) => n.status === "unread");
    return base.filter((n) => getConfig(n.title).type === activeTab);
  };

  const filtered = getFiltered();

  const getCount = (key) => {
    if (key === "all") return 0;
    if (key === "unread") return unreadCount;
    return visibleNotifs.filter(
      (n) => getConfig(n.title).type === key && n.status === "unread"
    ).length;
  };

  const handleTabClick = (key) => {
    setActiveTab(key);
  };

  const isComplaintTab = activeTab === "complaint";
  const customerComplaints = filtered.filter((n) => !n.chseller_id);
  const sellerComplaints = filtered.filter((n) => n.chseller_id);

  const renderCard = (n) => {
    const cfg = getConfig(n.title);
    const isUnread = n.status === "unread";
    return (
      <div
        key={n.id}
        className={`an-card ${cfg.className} ${isUnread ? "an-unread" : ""}`}
      >
        {isUnread && (
          <span className="an-dot" style={{ background: cfg.dotColor }} />
        )}
        <div className="an-card-icon">{cfg.icon}</div>
        <div className="an-card-body">
          <h4 className="an-card-title">{n.title}</h4>
          <p className="an-card-desc">{n.description}</p>
          <div className="an-card-meta">
            <span className={`an-card-pill ${cfg.className}-pill`}>
              {cfg.label}
            </span>
            <span className="an-card-time">
              {new Date(n.created_at).toLocaleString("en-PK")}
            </span>
            <span className="an-card-ago">{formatTime(n.created_at)}</span>
          </div>
        </div>
        <button
          className="an-delete-btn"
          onClick={() => deleteNotif(n.id)}
          title="Delete"
        >
          <FiTrash2 size={13} />
        </button>
      </div>
    );
  };

  return (
    <AdminMasterLayout>
      <div className="an-shell">
        <main className="an-main">
          <section className="an-card-shell">

            {/* Header */}
            <div className="an-header">
              <div className="an-header-left">
                <h1 className="an-page-title">Notifications</h1>
                {unreadCount > 0 && (
                  <span className="an-unread-badge">{unreadCount} unread</span>
                )}
              </div>
              <div className="an-header-actions">
                {unreadCount > 0 && (
                  <button className="an-btn" onClick={markAllRead}>
                    <FiCheck size={14} /> Mark all as read
                  </button>
                )}
                {visibleNotifs.length > 0 && (
                  <button className="an-btn an-btn-danger" onClick={clearAll}>
                    <FiTrash2 size={14} /> Clear all
                  </button>
                )}
              </div>
            </div>

            {/* Tabs */}
            <div className="an-tabs">
              {TABS.map((tab) => {
                const count = getCount(tab.key);
                const showCount = count > 0;
                return (
                  <button
                    key={tab.key}
                    className={`an-tab ${activeTab === tab.key ? "active" : ""}`}
                    onClick={() => handleTabClick(tab.key)}
                  >
                    {tab.label}
                    {showCount && (
                      <span className="an-tab-count">{count}</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Loading */}
            {loading && (
              <div className="an-empty">
                <div className="an-spinner" />
                <p>Loading...</p>
              </div>
            )}

            {/* Empty */}
            {!loading && filtered.length === 0 && (
              <div className="an-empty">
                <FiBell size={56} />
                <h3>No notifications</h3>
                <p>You're all caught up!</p>
              </div>
            )}

            {/* Complaints — split left/right */}
            {!loading && filtered.length > 0 && isComplaintTab ? (
              <div className="an-split-layout">
                <div className="an-split-col">
                  <div className="an-split-heading">
                    <FiUser size={13} /> Customer Complaints
                    <span className="an-split-count">
                      {customerComplaints.length}
                    </span>
                  </div>
                  <div className="an-list">
                    {customerComplaints.length > 0 ? (
                      customerComplaints.map(renderCard)
                    ) : (
                      <p className="an-split-empty">No customer complaints</p>
                    )}
                  </div>
                </div>
                <div className="an-split-col">
                  <div className="an-split-heading">
                    <FiShoppingBag size={13} /> Seller Complaints
                    <span className="an-split-count">
                      {sellerComplaints.length}
                    </span>
                  </div>
                  <div className="an-list">
                    {sellerComplaints.length > 0 ? (
                      sellerComplaints.map(renderCard)
                    ) : (
                      <p className="an-split-empty">No seller complaints</p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              !loading &&
              filtered.length > 0 && (
                <div className="an-list">{filtered.map(renderCard)}</div>
              )
            )}

          </section>
        </main>
      </div>
    </AdminMasterLayout>
  );
};

export default Notifications;